import React from 'react'
import { Box, makeStyles, Toolbar } from '@material-ui/core'
import { useReactiveVar } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { BuildingFragment } from '../../generated/graphql'
import { BuildingDrawerContent } from './BuildingDrawerContent'
import { SystemContainerDrawer } from '../system-container/SystemContainerDrawer'
import { Sidebar } from '../layout/Sidebar'
import { SystemContainerSidebarContent } from '../system-container/sidebar/SystemContainerSidebarContent'
import {
  SystemContainerHeader,
  useHeaderStyles,
} from '../system-container/SystemContainerHeader'
import {
  activeVersionVar,
  calculatorVar,
  comparisonVersionCalculatorVar,
  comparisonVersionVar,
} from '../../apollo/reactive-vars'
import {
  buildingResultCategories,
  buildingResultCategoriesWithoutLocalEnergy,
} from '../../provider/result-categories'

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    height: '100%',
  },
})

type Props = {
  building?: BuildingFragment
  onClose: () => void
}

export function BuildingDrawer({
  building,
  onClose,
}: Props): React.ReactElement {
  const classes = useStyles()
  const headerClasses = useHeaderStyles()
  const { t } = useTranslation()
  const calculator = useReactiveVar(calculatorVar)
  const activeVersion = useReactiveVar(activeVersionVar)
  const comparisonCalculator = useReactiveVar(comparisonVersionCalculatorVar)
  const comparisonVersion = useReactiveVar(comparisonVersionVar)

  const containers =
    calculator
      ?.getNonMultipliedBuildings()
      .filter((buildingCalculator) => buildingCalculator.id === building?.id) ??
    []
  const comparisonVersionBuilding = comparisonVersion?.buildings.find(
    (comparisonBuilding) => comparisonBuilding.name === building?.name,
  )
  const comparisonContainers =
    comparisonCalculator
      ?.getNonMultipliedBuildings()
      .filter(
        (buildingCalculator) =>
          buildingCalculator.id === comparisonVersionBuilding?.id,
      ) ?? undefined

  const hasLocalEnergy =
    (activeVersion?.localGrids?.length ?? 0) > 0 ||
    Boolean(activeVersion?.energyBus) ||
    (comparisonVersion?.localGrids?.length ?? 0) > 0 ||
    Boolean(comparisonVersion?.energyBus)
  const filteredResultCategories = hasLocalEnergy
    ? buildingResultCategories
    : buildingResultCategoriesWithoutLocalEnergy

  return (
    <SystemContainerDrawer isOpen={building !== undefined} onClose={onClose}>
      <SystemContainerHeader
        closeLabel={t('building.close')}
        onClose={onClose}
        containerName={building?.name ?? ''}
      />

      {building && (
        <Box display="flex">
          <Sidebar isExtended={comparisonContainers !== undefined}>
            {/* Allocate space for app bar. */}
            <Toolbar className={headerClasses.toolbar} />

            <SystemContainerSidebarContent
              containers={containers}
              comparisonContainers={comparisonContainers}
              resultCategories={filteredResultCategories}
            />
          </Sidebar>

          <main className={classes.content}>
            {/* Allocate space for app bar. */}
            <Toolbar className={headerClasses.toolbar} />

            <Box flexGrow={1}>
              <BuildingDrawerContent building={building} onClose={onClose} />
            </Box>
          </main>
        </Box>
      )}
    </SystemContainerDrawer>
  )
}
